import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";

/**
 * Tabla de verdad de un par de términos (Tabla 12).
 *
 * Réplica de graficar_tabla_verdad(term1, term2, heuristic_scores) del
 * notebook del autor: una celda por componente LEL con (Sí) o (No) según si
 * su similitud alcanzó el umbral, y debajo el estado resultante del par.
 *
 * Umbrales por componente, como en combined_similarity_embeddings():
 *   Nombre  -> threshold + 0.05
 *   Tipo    -> coincidencia exacta del tipo
 *   Noción  -> threshold - 0.10
 *   Impacto -> threshold - 0.10
 *
 * Recibe `heuristic` de /api/term-detail: { nombre, tipo, nocion, impacto }.
 */

// Paleta del notebook: azul oscuro si cumple, celeste si no.
const COLOR_SI = "#00008B";
const COLOR_NO = "#87CEFA";

// Color de la caja del estado resultante.
const COLOR_ESTADO = {
  Similitud: "#00008B",
  Diferencia: "#1E90FF",
  "No similitud": "#87CEFA",
  Omisión: "#87CEFA",
};

const COLUMNAS = [
  { clave: "nombre", etiqueta: "Nombre", ayuda: "Similitud del nombre ≥ umbral + 0.05" },
  { clave: "tipo", etiqueta: "Tipo", ayuda: "Coincidencia exacta del tipo de símbolo" },
  { clave: "nocion", etiqueta: "Noción", ayuda: "Similitud de nociones ≥ umbral − 0.10" },
  { clave: "impacto", etiqueta: "Impacto", ayuda: "Similitud de impactos ≥ umbral − 0.10" },
];

const TablaVerdad = ({ heuristic, estado }) => {
  if (!heuristic) return null;

  const colorEstado = COLOR_ESTADO[estado] || COLOR_ESTADO["No similitud"];
  // Sobre celeste el texto blanco no se lee: se usa un azul oscuro.
  const textoEstado = colorEstado === "#87CEFA" ? "#0B3B52" : "#FFFFFF";

  return (
    <Box className="bg-white rounded-lg p-4 shadow-sm">
      <Typography
        sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#374151", mb: 1.5 }}
      >
        Tabla de Verdad
      </Typography>

      {/* Una celda por componente */}
      <Box sx={{ display: "flex", gap: 1 }}>
        {COLUMNAS.map(({ clave, etiqueta, ayuda }) => {
          const cumple = Boolean(heuristic[clave]);
          return (
            <Tooltip key={clave} title={ayuda} arrow>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    backgroundColor: cumple ? COLOR_SI : COLOR_NO,
                    borderRadius: "4px",
                    py: 1.25,
                    textAlign: "center",
                    cursor: "help",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: cumple ? "#FFFFFF" : "#0B3B52",
                    }}
                  >
                    {cumple ? "(Sí)" : "(No)"}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#6B7280",
                    textAlign: "center",
                    mt: 0.5,
                  }}
                >
                  {etiqueta}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Estado resultante del par */}
      {estado && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
          <Box
            sx={{
              backgroundColor: colorEstado,
              borderRadius: "999px",
              px: 2,
              py: 0.75,
            }}
          >
            <Typography
              sx={{ fontSize: "0.78rem", fontWeight: 700, color: textoEstado }}
            >
              Estado Resultante: {estado.toUpperCase()}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TablaVerdad;
